import React, { useCallback, useMemo, useState } from 'react';
import { Button, TextField, CircularProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LocalTranslatedText from '@/translation/frontend/components/LocalTranslatedText';
import api from '../../web_chat/frontend/apis';
import './EmailDraftReviewCard.scss';

const TERMINAL_STATUSES = {
  sent: { label: 'Sent', icon: 'check' },
  deleted: { label: 'Deleted', icon: 'check' },
  updated: { label: 'Updated', icon: 'check' },
};

/**
 * Renders an AI-generated email draft (plus the email it replies to) with
 * Edit / Send / Delete actions. Clicking an action calls the deterministic
 * POST /conversation/{id}/email_draft_action endpoint with the exact draft_id
 * carried in the event metadata, so it is unambiguous even with multiple drafts.
 */
export default function EmailDraftReviewCard({
  review,
  conversationId,
  conversationToken,
  webChatConfiguration,
}) {
  const draft = review?.draft || {};
  const incoming = review?.incoming || {};
  const hasDraftId = Boolean(review?.draft_id?.trim?.() ?? review?.draft_id);

  const [status, setStatus] = useState(review?.status || 'pending');
  const [pendingAction, setPendingAction] = useState(null);
  const [error, setError] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [subject, setSubject] = useState(draft.subject || '');
  const [body, setBody] = useState(draft.body || '');

  const accentColor =
    webChatConfiguration?.role_icon_color || 'rgb(37, 99, 235)';

  const isTerminal = useMemo(
    () => Object.prototype.hasOwnProperty.call(TERMINAL_STATUSES, status),
    [status]
  );

  const performAction = useCallback(
    async (action, extra = {}) => {
      if (!conversationId) {
        setError('Missing conversation context.');
        return;
      }
      if (!hasDraftId) {
        setError(
          'This draft is missing its Gmail draft id. Send a new test email after the latest deploy, or use Gmail directly.'
        );
        return;
      }
      setPendingAction(action);
      setError(null);
      try {
        const result = await api.emailDraftAction(
          conversationId,
          conversationToken,
          {
            action,
            draft_id: review?.draft_id,
            email_owner_user_id: review?.email_owner_user_id,
            email_provider: review?.email_provider,
            ...extra,
          }
        );
        if (result?.success) {
          setStatus(result.status || action);
          if (action === 'edit') {
            setIsEditing(false);
          }
        } else {
          setError(
            result?.error || result?.message || 'The action could not be completed.'
          );
        }
      } catch (e) {
        setError(
          e?.response?.data?.message ||
            e?.message ||
            'The action could not be completed.'
        );
      } finally {
        setPendingAction(null);
      }
    },
    [
      conversationId,
      conversationToken,
      review?.draft_id,
      review?.email_owner_user_id,
      review?.email_provider,
      hasDraftId,
    ]
  );

  const handleSend = useCallback(() => performAction('send'), [performAction]);
  const handleDelete = useCallback(
    () => performAction('delete'),
    [performAction]
  );
  const handleSaveEdit = useCallback(
    () => performAction('edit', { subject, body }),
    [performAction, subject, body]
  );
  const handleStartEdit = useCallback(() => {
    setSubject(draft.subject || '');
    setBody(draft.body || '');
    setIsEditing(true);
    setError(null);
  }, [draft.subject, draft.body]);
  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setError(null);
  }, []);

  const busy = pendingAction !== null;

  return (
    <div className={'email-draft-review-card'}>
      {incoming && (incoming.from || incoming.subject || incoming.preview) ? (
        <div className={'email-draft-incoming'}>
          <div className={'email-draft-incoming-label'}>
            <LocalTranslatedText text={'In reply to'} />
          </div>
          {incoming.from ? (
            <div className={'email-draft-row'}>
              <span className={'email-draft-row-label'}>
                <LocalTranslatedText text={'From'} />
              </span>
              <span className={'email-draft-row-value'}>{incoming.from}</span>
            </div>
          ) : null}
          {incoming.subject ? (
            <div className={'email-draft-row'}>
              <span className={'email-draft-row-label'}>
                <LocalTranslatedText text={'Subject'} />
              </span>
              <span className={'email-draft-row-value'}>
                {incoming.subject}
              </span>
            </div>
          ) : null}
          {incoming.preview ? (
            <div className={'email-draft-incoming-preview'}>
              {incoming.preview}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={'email-draft-section'} style={{ borderColor: accentColor }}>
        <div className={'email-draft-section-label'} style={{ color: accentColor }}>
          <LocalTranslatedText text={'Suggested reply'} />
        </div>

        {isEditing ? (
          <>
            <TextField
              className={'email-draft-edit-field'}
              label={'Subject'}
              variant={'outlined'}
              size={'small'}
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={busy}
              margin={'dense'}
            />
            <TextField
              className={'email-draft-edit-field'}
              label={'Message'}
              variant={'outlined'}
              size={'small'}
              fullWidth
              multiline
              minRows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={busy}
              margin={'dense'}
            />
          </>
        ) : (
          <>
            {draft.subject ? (
              <div className={'email-draft-row'}>
                <span className={'email-draft-row-label'}>
                  <LocalTranslatedText text={'Subject'} />
                </span>
                <span className={'email-draft-row-value'}>{draft.subject}</span>
              </div>
            ) : null}
            {draft.body ? (
              <div className={'email-draft-body'}>{draft.body}</div>
            ) : (
              <div className={'email-draft-body email-draft-body-empty'}>
                <LocalTranslatedText text={'Draft content is ready in Gmail. Use Edit to change it here before sending.'} />
              </div>
            )}
          </>
        )}
      </div>

      {error ? (
        <div className={'email-draft-error'}>
          <ErrorOutlineIcon sx={{ fontSize: 16 }} />
          <span>{error}</span>
        </div>
      ) : null}

      {isTerminal ? (
        <div className={'email-draft-status'}>
          <CheckCircleIcon sx={{ fontSize: 18, color: 'rgb(22, 163, 74)' }} />
          <LocalTranslatedText text={TERMINAL_STATUSES[status].label} />
        </div>
      ) : (
        <div className={'email-draft-actions'}>
          {isEditing ? (
            <>
              <Button
                variant={'contained'}
                size={'small'}
                onClick={handleSaveEdit}
                disabled={busy || !body.trim()}
                startIcon={
                  pendingAction === 'edit' ? (
                    <CircularProgress size={14} color={'inherit'} />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
                sx={{ backgroundColor: accentColor }}
              >
                <LocalTranslatedText text={'Save'} />
              </Button>
              <Button
                variant={'outlined'}
                size={'small'}
                onClick={handleCancelEdit}
                disabled={busy}
              >
                <LocalTranslatedText text={'Cancel'} />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant={'contained'}
                size={'small'}
                onClick={handleSend}
                disabled={busy || !hasDraftId}
                startIcon={
                  pendingAction === 'send' ? (
                    <CircularProgress size={14} color={'inherit'} />
                  ) : (
                    <SendIcon />
                  )
                }
                sx={{ backgroundColor: accentColor }}
              >
                <LocalTranslatedText text={'Send'} />
              </Button>
              <Button
                variant={'outlined'}
                size={'small'}
                onClick={handleStartEdit}
                disabled={busy}
                startIcon={<EditIcon />}
              >
                <LocalTranslatedText text={'Edit'} />
              </Button>
              <Button
                variant={'outlined'}
                color={'error'}
                size={'small'}
                onClick={handleDelete}
                disabled={busy || !hasDraftId}
                startIcon={
                  pendingAction === 'delete' ? (
                    <CircularProgress size={14} color={'inherit'} />
                  ) : (
                    <DeleteOutlineIcon />
                  )
                }
              >
                <LocalTranslatedText text={'Delete'} />
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
